import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { WithStyles, makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
// components
import {
    AttachFile,
    Send,
    Visibility,
    Error,
    Cancel,
    CheckCircle,
    CalendarToday,
    EventAvailable,
    FiberManualRecord,
    BorderColor,
    KeyboardArrowDown,
    Sms,
    Layers,
} from '@material-ui/icons';
import Button from '@material-ui/core/Button';
import Input from '@material-ui/core/Input';
import TextField from '@material-ui/core/TextField';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { TooltipButton } from '../tooltip-button';
import ErrorMessage from '../error-message';
import FormSubmit from '../form/formSubmit';
// modules
import { actions as documentActions, selectors as documentSelectors } from '../../redux/reducers/documents';
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import { FileUpload, DocumentType, DocumentMode, DocumentState } from '../../redux/reducers/api-types';
// ressources
import withGlobalStyles from '../../utils/global-styles';
import DateUtils from '../../utils/dateUtils';
import { clsx } from '../../utils/htmlUtils';
import { generateDocumentUrl } from '../../utils/businessUtils';
import { MAX_UPLOAD_SIZE, prepareUploadFile } from '../../utils/fileUtils';
import StringUtils from '../../utils/stringUtils';
import {
    ACCEPTED_UPLOAD_TYPE,
    blue,
    dateMask,
    dateShort,
    green,
    grey,
    lightBlue,
    orange,
    red,
} from '../../utils/constants';

interface DocumentOwnProps {
    className?: string;
    id: number;
    name: string;
    docClass: string;
    docType: string;
    mode?: DocumentMode;
    readOnly?: boolean;
    startDate?: boolean;
    startLabel?: string;
    endDate?: boolean;
    endDateDisable?: boolean;
    lastControlDate?: boolean;
    controlDate?: boolean;
    maxDate?: number;
    datas?: any[];
    noLabel?: boolean;
    classId: number;
}
interface DocumentReduxProps extends WithStyles, WithTranslation {
    globalClasses: any;
    documents: DocumentType;
    getDocumentError: ErrorPayload;
    sendSMS: (id: string, idPartner: string, idSignatories: string, docType?: string) => any;
    updateDocVersion: (id: string, payload: any, docType?: string) => any;
    upsertDoc: (type: any /* ??? */, payload: any) => any;
    activateDocumentVersion: (
        documentId: string,
        versionId: string,
        docType: string,
        docClass: string,
        classId: number,
    ) => any;
}

type DocumentProps = DocumentOwnProps & DocumentReduxProps;

const InlineDocumentComponent = (props: DocumentProps): JSX.Element => {
    const classes = useStyles();
    const {
        t,
        documents,
        getDocumentError,
        globalClasses,
        docClass,
        docType,
        classId,
        mode,
        datas,
        id,
        readOnly,
        startDate,
        startLabel,
        endDate,
        endDateDisable,
        lastControlDate,
        controlDate,
        maxDate,
        noLabel,
    } = props;

    // Get the doc from id when it was already created in the other case we have to find it
    const documentData = id
        ? documents[id]
        : Object.values(documents).find(
              (d) => d.document_class === docClass && d.document_type === docType && d[`${docClass}_id`] === classId,
          );
    const documentVersion = documentData?.current_version;
    const dateStart = documentVersion?.start_date ? new Date(documentVersion?.start_date * 1000) : null;
    const dateEnd = documentVersion?.end_date ? new Date(documentVersion?.end_date * 1000) : null;
    const dateLastControl = documentData?.last_control_date ? new Date(documentData?.last_control_date * 1000) : null;
    const dateControl = documentData?.control_date ? new Date(documentData?.control_date * 1000) : null;
    const creationDate = documentData?.creation_date;
    const modificationDate = documentVersion?.upload_at;
    // const modificationUser = documentVersion?.user_id;
    const documentState = documentVersion?.state;

    const inputRef = React.useRef();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [file, setFile] = React.useState<FileUpload>(null);

    const [errorMode, setErrorMode] = React.useState<boolean>(false);
    const [errorMissingDate, setErrorMissingDate] = React.useState<boolean>(false);
    const [fileTooLarge, setFileTooLarge] = React.useState<boolean>(false);

    const [selectedDate, handleDateChange] = React.useState<Date>(dateEnd);
    const [open, setOpen] = React.useState<boolean>(false);

    const [selectedStartDate, handleDateStartChange] = React.useState<Date>(dateStart);
    const [openStartDate, setOpenStartDate] = React.useState<boolean>(false);

    const [selectedLastControlDate, handleLastControlDateChange] = React.useState<Date>(dateLastControl);
    const [openLastControlDate, setOpenLastControlDate] = React.useState<boolean>(false);

    const [selectedControlDate, handleControlDateChange] = React.useState<Date>(dateControl);
    const [openControlDate, setOpenControlDate] = React.useState<boolean>(false);

    // TODO: do something better to update dates that are not up to date after an upsert
    useEffect(() => {
        const data = id
            ? documents[id]
            : Object.values(documents).find(
                  (d) =>
                      d.document_class === docClass && d.document_type === docType && d[`${docClass}_id`] === classId,
              );
        const dateEnd = data?.current_version?.end_date ? new Date(data?.current_version?.end_date * 1000) : null;
        if (dateEnd && selectedDate !== dateEnd) {
            handleDateChange(dateEnd);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents, id, docClass, docType, classId]);

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const showMenuItemOptions = () => (e: React.MouseEvent) => {
        e.preventDefault();
        setAnchorEl({ elt: e.target });
    };

    function hideMenuItemOptions(e: React.MouseEvent) {
        e.preventDefault();
        setAnchorEl(null);
    }

    const sendSMS = (idSignatories) => (e) => {
        props.sendSMS(documentData.id, documentData.partner_id, idSignatories, docType);
    };
    function activateDocumentVersion(e) {
        props.activateDocumentVersion(documentData.id, documentData.current_version.id, docType, docClass, classId);
    }
    function updateStatus(docStatus) {
        setAnchorEl(null);
        props.updateDocVersion(documentData.id, { state: docStatus }, docType);
    }

    const onCloseStartDate = (startDateData: Date) => {
        const formData = new FormData();
        if (startDateData) {
            formData.set('version[start_date]', StringUtils.formatDocumentData(startDateData.toLocaleDateString()));
        }
        updateDocumentDate(formData);
    };
    const onCloseEndDate = (endDateData: Date) => {
        const formData = new FormData();
        if (endDateData) {
            formData.set('version[end_date]', StringUtils.formatDocumentData(endDateData.toLocaleDateString()));
        }
        updateDocumentDate(formData);
    };
    const onCloseControlDate = (controlDateData: Date) => {
        const formData = new FormData();
        if (controlDateData) {
            formData.set('control_date', StringUtils.formatDocumentData(controlDateData.toLocaleDateString()));
        }
        updateDocumentDate(formData);
    };
    const onCloseLastControlDate = (lastControlDateData: Date) => {
        const formData = new FormData();
        if (lastControlDateData) {
            formData.set('last_control_date', StringUtils.formatDocumentData(lastControlDateData.toLocaleDateString()));
        }
        updateDocumentDate(formData);
    };

    const updateDocumentDate = (formData) => {
        if (documentData?.id) {
            formData.append('document_class', docClass);
            formData.append('document_type', docType);
            formData.append(`${docClass}_id`, '' + classId);

            props.upsertDoc(docType, formData);
        }
    };

    function handleDocumentChange(e) {
        setFileTooLarge(false);
        if (e.target.files.length) {
            const arrFiles = Array.from(e.target.files);
            const files: FileUpload[] = arrFiles.map((file: any, index) => {
                const src = window.URL.createObjectURL(file);
                return { file, id: index, src };
            });
            // console.log(files);
            setFile(files[0]);
        }
    }

    async function handleUploadDocument(e) {
        e.preventDefault();
        const formData = new FormData(e.target);

        formData.append('document_class', docClass);
        formData.append('document_type', docType);
        formData.append(`${docClass}_id`, '' + classId);

        const startDateData = formData.get('version[start_date]');
        const endDateData = formData.get('version[end_date]');
        const lastControlDateData = formData.get('last_control_date');
        const controlDateData = formData.get('control_date');
        if (startDateData) {
            formData.set('version[start_date]', StringUtils.formatDocumentData(startDateData.toString()));
        }
        if (endDateData) {
            formData.set('version[end_date]', StringUtils.formatDocumentData(endDateData.toString()));
        }
        if (lastControlDateData) {
            formData.set('last_control_date', StringUtils.formatDocumentData(lastControlDateData.toString()));
        }
        if (controlDateData) {
            formData.set('control_date', StringUtils.formatDocumentData(controlDateData.toString()));
        }

        // Check if we should have a value or not
        const missingDate =
            (startDate && !startDateData) ||
            (endDate && !endDateData) ||
            (lastControlDate && !lastControlDateData) ||
            (controlDate && !controlDateData);

        // console.log('la', missingDate, file);
        if (missingDate || !file) {
            setErrorMissingDate(missingDate);
            setErrorMode(true);
        } else {
            setErrorMode(false);
            setErrorMissingDate(false);
            const fileFits = await prepareUploadFile(formData, 'version[file]');
            setFileTooLarge(!fileFits);
            if (fileFits) {
                props.upsertDoc(docType, formData);
            }
        }
    }

    //=========================================//
    //=============== Generator ===============//
    //=========================================//

    // const createFilePicker = (): JSX.Element => {
    //     return (
    //         <Button
    //             variant="contained"
    //             component="label"
    //             className={classes.buttonAction}
    //             style={{
    //                 backgroundColor: 'blueviolet',
    //             }}
    //         >
    //             <div className={classes.button}>
    //                 <AttachFile />
    //                 <div className={classes.buttonText}>{t('file')}</div>
    //             </div>
    //             <Input
    //                 className="file"
    //                 type="file"
    //                 name="version[file]"
    //                 inputProps={{ accept: ACCEPTED_UPLOAD_TYPE }}
    //                 onChange={handleDocumentChange}
    //                 style={{ display: 'none' }}
    //             />
    //         </Button>
    //     );
    // };

    const createButton = (props: any): JSX.Element => {
        const { backgroundColor, text, icon, ...rest } = props;
        return (
            <Button
                variant="contained"
                className={classes.buttonAction}
                style={{ backgroundColor: backgroundColor }}
                {...(rest as any)}
            >
                <div className={classes.button}>
                    {icon}
                    <div className={classes.buttonText}>{text}</div>
                </div>
            </Button>
        );
    };

    const createDatePicker = (props: any): JSX.Element => {
        const { date, disabled, key, name, label, data, changeData, isOpen, changeOpen, onClose } = props;

        let backgroundColor = data ? green : errorMode ? orange : lightBlue;
        if (
            ['last_control_date', 'control_date', 'version[end_date]'].includes(name) &&
            data &&
            data < new Date() &&
            !errorMode
        ) {
            backgroundColor = red;
        }
        const icon = data ? <EventAvailable /> : <CalendarToday />;
        let maxDateString = DateUtils.dateToString(DateUtils.addYears(100)); // Default +100y
        // if (maxDate && name === 'version[end_date]') { // to only max specific dates
        if (maxDate) {
            maxDateString = DateUtils.dateToString(
                modificationDate
                    ? DateUtils.addYears(maxDate, new Date(modificationDate * 1000))
                    : DateUtils.addYears(maxDate),
            );
        }

        return (
            date && (
                <Button
                    key={key}
                    disabled={disabled}
                    variant="contained"
                    className={classes.buttonAction}
                    style={{ backgroundColor: backgroundColor }}
                    onClick={() => {
                        if (!readOnly) {
                            changeOpen(true);
                        }
                    }}
                >
                    <div className={classes.button}>
                        <div className={classes.button} onClick={(e) => (isOpen ? e.stopPropagation() : {})}>
                            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                                {icon}
                                <div className={classes.buttonText}>{label}</div>
                                <DatePicker
                                    value={data}
                                    onChange={(e) => {
                                        setErrorMode(false);
                                        setErrorMissingDate(false);
                                        onClose(e);
                                        changeData(e);
                                    }}
                                    format={dateShort}
                                    open={isOpen}
                                    onClose={() => {
                                        if (!readOnly) {
                                            changeOpen(false);
                                        }
                                    }}
                                    maxDate={maxDateString}
                                    cancelLabel={t('common:cancel')}
                                    okLabel={t('common:ok')}
                                    required={true}
                                    name={name}
                                    className={classes.datePicker}
                                    inputProps={{
                                        required: true,
                                        style: {
                                            fontSize: 8,
                                            maxWidth: 50,
                                            color: 'white',
                                            fontWeight: 500,
                                            boxSizing: 'border-box',
                                            textAlign: 'center',
                                            display: data ? 'block' : 'none',
                                        },
                                    }}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                    </div>
                </Button>
            )
        );
    };

    //========================================//
    //=============== Creators ===============//
    //========================================//

    const createSend = createButton({
        backgroundColor: blue,
        text: t('add'),
        icon: <Send />,
        type: 'submit',
    });

    const createValidation = createButton({
        backgroundColor: orange,
        text: t('pending_validation'),
        icon: <Error />,
        onClick: showMenuItemOptions(),
    });
    const createInvalide = createButton({
        backgroundColor: red,
        text: t('status.terminated'),
        icon: <Cancel />,
        onClick: showMenuItemOptions(),
    });
    const createValide = createButton({
        backgroundColor: green,
        text: t('status.active'),
        icon: <CheckCircle />,
        onClick: showMenuItemOptions(),
    });

    const createShowSMS = createButton({
        backgroundColor: orange,
        text: t('sign'),
        icon: <KeyboardArrowDown />,
        onClick: activateDocumentVersion,
    });

    const inputsDate = [
        createDatePicker({
            date: startDate,
            key: 'startDate',
            name: 'version[start_date]',
            label: startLabel ?? t('start_date'),
            data: selectedStartDate,
            changeData: handleDateStartChange,
            isOpen: openStartDate,
            changeOpen: setOpenStartDate,
            onClose: onCloseStartDate,
        }),
        createDatePicker({
            date: endDate,
            disabled: endDateDisable,
            key: 'endDate',
            name: 'version[end_date]',
            label: t('end_date'),
            data: selectedDate,
            changeData: handleDateChange,
            isOpen: open,
            changeOpen: setOpen,
            onClose: onCloseEndDate,
        }), // Date until when the document is valid
        createDatePicker({
            date: lastControlDate,
            key: 'lastControlDate',
            name: 'last_control_date',
            label: t('last_control_date'),
            data: selectedLastControlDate,
            changeData: handleLastControlDateChange,
            isOpen: openLastControlDate,
            changeOpen: setOpenLastControlDate,
            onClose: onCloseLastControlDate,
        }), // Date for last control of the document
        createDatePicker({
            date: controlDate,
            key: 'controlDate',
            name: 'control_date',
            label: t('control_date'),
            data: selectedControlDate,
            changeData: handleControlDateChange,
            isOpen: openControlDate,
            changeOpen: setOpenControlDate,
            onClose: onCloseControlDate,
        }), // Date for next needed control of the document
    ];

    // const endAndControlDates = documentData && (
    //     <>
    //         {createDateInfo(documentData.control_date, t('control_date'))}
    //         {createDateInfo(documentData.last_control_date, t('last_control_date'))}
    //         {createDateInfo(documentData.current_version?.end_date, t('end_date'))}
    //     </>
    // );

    // const createDateInfo = (time, label) => {
    //     return (
    //         time && (
    //             <div className={`classes.documentInfo classes.documentDate`}>
    //                 <strong>{label}</strong> : <span>{DateUtils.dateFormat(time * 1000, 'yyyy-mm-dd')}</span>
    //             </div>
    //         )
    //     );
    // };

    const viewDocument = documentData?.current_version?.id && (
        <div className={classes.fileLink}>
            {createButton({
                backgroundColor: grey,
                text: t('download'),
                icon: <Visibility />,
                href: generateDocumentUrl(documentData.current_version.id),
                target: '_blank',
                rel: 'noreferrer noopener',
            })}
        </div>
    );

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const handleOnClick = () => {
        if (inputRef !== undefined && inputRef.current !== undefined) {
            const current = inputRef.current as any;
            current.click();
        }
    };

    const handleStopPropagation = (e) => {
        e.stopPropagation();
    };

    //======================================//
    //=============== Render ===============//
    //======================================//

    const renderItemMenu = (): JSX.Element => {
        return (
            <Menu
                id="simple-menu"
                anchorEl={anchorEl?.elt}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={hideMenuItemOptions}
            >
                {anchorEl && documentState !== DocumentState.Pending && documentState !== DocumentState.Created && (
                    <MenuItem onClick={() => updateStatus(DocumentState.Pending)}>
                        <FiberManualRecord fontSize="small" className={classes.menuPending} />
                        {t('status.pending')}
                    </MenuItem>
                )}
                {anchorEl && documentState !== DocumentState.Active && (
                    <MenuItem onClick={() => updateStatus(DocumentState.Active)}>
                        <FiberManualRecord fontSize="small" className={classes.menuValid} />
                        {t('active')}
                    </MenuItem>
                )}
                {anchorEl && documentState !== DocumentState.Terminated && (
                    <MenuItem onClick={() => updateStatus(DocumentState.Terminated)}>
                        <FiberManualRecord fontSize="small" className={classes.menuInvalid} />
                        {t('terminate')}
                    </MenuItem>
                )}
            </Menu>
        );
    };

    let cardContent = null;
    let rootClass = classes.root;
    if (props.className) {
        rootClass += ' ' + props.className;
    }

    if (mode && mode === DocumentMode.SMS) {
        if (!documentState) {
            return null;
        }
        if (readOnly) {
            cardContent = viewDocument;
        } else {
            cardContent = (
                <>
                    {viewDocument}

                    {DocumentState.Signing === documentState && (
                        <div style={{ display: 'flex' }}>
                            {documentData.current_version?.signatories?.map((s, index) => {
                                const fullname = datas.find((m) => m.id === s.user_id).fullname;
                                return (
                                    <div style={{ display: 'flex' }} key={`${s.user_id}-${index}`}>
                                        <TooltipButton title={`${t('sign_link')} de ${fullname}`}>
                                            {createButton({
                                                backgroundColor: orange,
                                                text: `${t('sign_link')}`,
                                                icon: <BorderColor />,
                                                href: s.sign_link,
                                                target: '_blank',
                                                rel: 'noreferrer noopener',
                                            })}
                                        </TooltipButton>
                                        <TooltipButton title={`${t('sms')}: ${t(`smsStatus.${s.sms_status}`)}`}>
                                            {createButton({
                                                backgroundColor: 'sent' === s.sms_status ? green : orange,
                                                text: `${t(`smsStatus.${s.sms_status}`)}`,
                                                icon: <Sms />,
                                                onClick: sendSMS(index),
                                            })}
                                        </TooltipButton>
                                    </div>
                                );
                            })}
                            {documentData.current_version && !documentData.current_version.signatories && (
                                <TooltipButton title={t('show')}>{createShowSMS}</TooltipButton>
                            )}
                        </div>
                    )}
                    {DocumentState.Active === documentState && (
                        <TooltipButton title={`${t('status.title')} : ${t('status.' + documentState)}`}>
                            {createButton({
                                backgroundColor: orange,
                                text: t('status.' + documentState),
                                icon: <Layers />,
                            })}
                        </TooltipButton>
                    )}
                </>
            );
        }
    } else if (mode && mode === DocumentMode.YOUSIGN) {
        if (!documentState) {
            return null;
        }
        if (readOnly) {
            cardContent = viewDocument;
        } else {
            const yousignLink =
                documentData.current_version?.snapcar_ref || documentData.yousign_data?.signers?.[0]?.signature_link;
            cardContent = (
                <>
                    {viewDocument}

                    {DocumentState.Signing === documentState && (
                        <div style={{ display: 'flex' }}>
                            {documentData.current_version &&
                                documentData.current_version.procedure_id &&
                                yousignLink && (
                                    <TooltipButton title={`${t('sign_waiting')}`}>
                                        {createButton({
                                            backgroundColor: orange,
                                            text: `${t('sign_waiting')}`,
                                            icon: <BorderColor />,
                                            href: yousignLink,
                                            target: '_blank',
                                            rel: 'noreferrer noopener',
                                        })}
                                    </TooltipButton>
                                )}
                            {documentData.current_version &&
                                (!documentData.current_version.procedure_id || !yousignLink) && (
                                    <TooltipButton title={t('show')}>{createShowSMS}</TooltipButton>
                                )}
                        </div>
                    )}
                    {DocumentState.Active === documentState && (
                        <TooltipButton title={`${t('status.title')} : ${t('status.' + documentState)}`}>
                            {createButton({
                                backgroundColor: green,
                                text: t('status.' + documentState),
                                icon: <Layers />,
                            })}
                        </TooltipButton>
                    )}
                </>
            );
        }
    } else if (documentVersion) {
        cardContent = (
            <>
                {viewDocument}
                {(DocumentState.Pending === documentState || DocumentState.Created === documentState) && (
                    <>
                        {readOnly ? (
                            createValidation
                        ) : (
                            <>
                                <TooltipButton title={t('hint_validation')} spanClassName={globalClasses.inlineFlex}>
                                    {createValidation}
                                </TooltipButton>
                                {renderItemMenu()}
                            </>
                        )}
                    </>
                )}
                {DocumentState.Pending !== documentState && DocumentState.Created !== documentState && (
                    <>
                        {readOnly ? (
                            DocumentState.Active === documentState ? (
                                createValide
                            ) : (
                                createInvalide
                            )
                        ) : (
                            <>
                                <TooltipButton title={t('hint_validation')} spanClassName={globalClasses.inlineFlex}>
                                    {DocumentState.Active === documentState ? createValide : createInvalide}
                                </TooltipButton>
                                {renderItemMenu()}
                            </>
                        )}
                    </>
                )}
            </>
        );
    }

    return (
        <div className={rootClass}>
            {!noLabel && props.name && (
                <div className={clsx(globalClasses.formLabel, classes.formLabel)}>{props.name}</div>
            )}
            <FormSubmit debounceSubmit={{ callback: handleUploadDocument }} className={classes.form}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    {!readOnly && (
                        <>
                            <TextField
                                variant="outlined"
                                required={true}
                                error={errorMode && !file}
                                style={{ verticalAlign: 'inherit', marginRight: 8, justifyContent: 'center' }}
                                onClick={handleOnClick}
                                inputProps={{
                                    style: {
                                        padding: '6px 0px',
                                        margin: '0 8px',
                                        height: 36,
                                        textOverflow: 'ellipsis',
                                    },
                                }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <label onClick={handleStopPropagation}>
                                                <AttachFile />
                                                <Input
                                                    ref={inputRef}
                                                    className="file"
                                                    type="file"
                                                    name="version[file]"
                                                    inputProps={{ accept: ACCEPTED_UPLOAD_TYPE }}
                                                    onChange={handleDocumentChange}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>
                                        </InputAdornment>
                                    ),
                                }}
                                value={file?.file?.name || t('form_update.select_file')}
                            />
                            {/* {createFilePicker()} */}
                            {createSend}
                        </>
                    )}
                    {cardContent}
                    {inputsDate}
                </div>
            </FormSubmit>
            {getDocumentError &&
                (getDocumentError.details?.document_type ? (
                    <div className={classes.error}>{getDocumentError.details.document_type}</div>
                ) : (
                    <ErrorMessage payload={getDocumentError} />
                ))}
            {errorMode && !file && <div className={classes.error}>{t('no_document')}</div>}
            {errorMode && errorMissingDate && <div className={classes.error}>{t('no_date')}</div>}
            {fileTooLarge && (
                <div className={classes.error}>{t('file_too_large', { max: MAX_UPLOAD_SIZE / (1024 * 1024) })}</div>
            )}
            {creationDate && (
                <div className={classes.documentInfo}>
                    <u>{`${t('creation_date')}`}</u>
                    {`: ${DateUtils.dateFormat(creationDate * 1000, dateMask)}`}
                </div>
            )}
            {modificationDate && creationDate !== modificationDate && (
                <div className={classes.documentInfo}>
                    <u>{`${t('update_date')}`}</u>
                    {`: ${DateUtils.dateFormat(modificationDate * 1000, dateMask)}`}
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            minWidth: '250px',
            // margin: theme.spacing(1),
            backgroundColor: 'white',
        },
        form: {
            alignContent: 'center',
            display: 'flex',
            flexDirection: 'column',
        },
        fileLink: {
            display: 'contents',
            alignItems: 'center',
            '& a': {
                color: [theme.palette.primary.main],
                '&:hover': {
                    color: [theme.palette.primary.light],
                },
            },
        },
        documentInfo: {
            fontSize: 12,
            color: theme.palette.colors.greyDark,
            fontWeight: 400,
            marginLeft: 16,
            marginTop: 3,
        },
        documentDate: {
            '& span': {
                whiteSpace: 'nowrap',
            },
        },
        formLabel: {
            marginBottom: 7,
        },
        button: {
            margin: 4,
            flexDirection: 'column',
            display: 'flex',
            alignItems: 'center',
            color: 'white',
        },
        buttonAction: {
            verticalAlign: 'inherit',
            marginRight: 3,
            padding: 0,
            minWidth: 48,
        },
        buttonText: {
            fontSize: 7,
            color: theme.palette.colors.white,
            fontWeight: 500,
        },
        menuValid: { color: theme.palette.colors.green, paddingRight: 10 },
        menuInvalid: { color: theme.palette.colors.red, paddingRight: 10 },
        menuPending: { color: theme.palette.colors.greyDark, paddingRight: 10 },
        datePicker: {
            '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                content: 'none',
            },
        },
        error: {
            fontSize: 12,
            color: theme.palette.danger.contrastText,
            fontWeight: 400,
            marginLeft: 16,
            marginTop: 3,
        },
    }),
);

const DocumentWithTranslation = withTranslation(['document', 'common'])(InlineDocumentComponent);

const DocumentWithStyleAndTranslation = withGlobalStyles(DocumentWithTranslation);

const mapStateToProps = (state, ownProps: DocumentOwnProps) => ({
    documents: documentSelectors.documents(state),
    getDocumentError: errorSelectors.getDocumentError(state, ownProps.docType),
});

const mapDispatchToProps = {
    sendSMS: documentActions.sendSMS,
    updateDocVersion: documentActions.updateDocVersion,
    upsertDoc: documentActions.upsertDoc,
    activateDocumentVersion: documentActions.activateDocumentVersion,
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentWithStyleAndTranslation);
